import React from "react";
import { createRoot } from "react-dom/client";
import { ClerkProvider } from "@clerk/clerk-react";
import App from "./App";
import { ApolloClient, InMemoryCache, ApolloProvider, createHttpLink, from } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { emitEmailNotVerified, hasEmailNotVerifiedCode } from "./lib/emailVerificationEvents";
import { CLERK_PUBLISHABLE_KEY, isClerkEnabled } from "./lib/clerk";
import "./styles/main.css";

const httpLink = createHttpLink({
  uri: process.env.REACT_APP_GRAPHQL_ENDPOINT || "http://localhost:8081/graphql",
  // SEC-1.10: send httpOnly dn_session cookie on same-site /graphql.
  credentials: "include",
});

const authLink = setContext((_, { headers }) => {
  // Dual-mode: prefer cookie (credentials include); still attach Bearer when
  // localStorage has a token (local cross-origin / older clients / e2e inject).
  const token = localStorage.getItem("token");
  return {
    headers: {
      ...headers,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  };
});

// Centralized fallback for sensitive mutations that are blocked server-side.
// The email-verification middleware returns a 403 with extensions.code
// EMAIL_NOT_VERIFIED (surfaced here as networkError.result.errors); some paths
// may also return it as a GraphQL error. In either case we emit an app event
// so the always-mounted banner opens the verify dialog instead of letting a
// raw error bubble to the user.
const emailVerifiedLink = onError(({ graphQLErrors, networkError }) => {
  if (hasEmailNotVerifiedCode(graphQLErrors)) {
    emitEmailNotVerified();
    return;
  }
  const netErrors = networkError && networkError.result && networkError.result.errors;
  if (hasEmailNotVerifiedCode(netErrors)) {
    emitEmailNotVerified();
  }
});

const client = new ApolloClient({
  link: from([emailVerifiedLink, authLink, httpLink]),
  cache: new InMemoryCache(),
});

const container = document.getElementById("root");
const root = createRoot(container);

const appTree = (
  <ApolloProvider client={client}>
    <App />
  </ApolloProvider>
);

root.render(
  isClerkEnabled() ? (
    <ClerkProvider publishableKey={CLERK_PUBLISHABLE_KEY} afterSignOutUrl="/">
      {appTree}
    </ClerkProvider>
  ) : (
    appTree
  )
);
